import { IPermissionGroup, PermissionGroup_Factory } from "csd.phoenix.models/PermissionGroup";
import { IPermissionGroupV3VM } from "csd.phoenix.models/PermissionGroupV3VM";
import { IPermissionGroupV3 } from "csd.phoenix.models/PermissionGroupV3";
import { IUser } from "csd.phoenix.models/User";
import { loadConfig } from "../../services/appConfigService";
import ODataApi from "../../services/api/odata";
import { ODataQueryParams } from "../../services/api/odata/models";
import http, { HttpError } from "../../services/api/http";
import { assertNonNil } from "../../utils/objectUtils";
import AsyncLruCache from "../../utils/AsyncLruCache";
import { findEFormsPermissionGroup, findAdminPermissionGroup} from "../../utils/permissionUtils";
import { cachedDataUpdatedSignal } from "./signals";
import { ICachedPermissionData } from "./models";


// Note: Non-expiring cache -- user will need to log pack in after permisison changes
const _cache: AsyncLruCache<"_", Map<string, IPermissionGroup>> = new AsyncLruCache<"_", Map<string, IPermissionGroup>>({ maxItems: 1, promiseFactory: loadGroups })
let _cachedData: ICachedPermissionData;

export class SchemaNotMigratedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "SchemaNotMigratedError";
  }
}

export function getCachedPermissionData(): ICachedPermissionData {
  if (!_cachedData) {
    throw new Error("Cached permission data not available!");
  }

  return _cachedData;
}



// Pre-calculated user permissions
function updateCachedValues(groups: IPermissionGroup[]) {
  const allPermissionGroups = new Map<string, IPermissionGroup[]>();

  if (!groups) {
    throw new Error("One or more groups must exist!");
  }
  
  // For each permission group ...
  groups.forEach(pg => {
    // For each user in group ...
    assertNonNil(pg.Users)
      .forEach(user => {
        const userId = assertNonNil(user.RecordId);

        // Per user permission groups
        let userPermissionGroups = allPermissionGroups.get(userId);
        if (!userPermissionGroups) {
          allPermissionGroups.set(userId, [pg])
        }
        else if (!userPermissionGroups.includes(pg)) {
          userPermissionGroups.push(pg);
        }
      })
  });

  _cachedData = {
    userPermissionGroups: allPermissionGroups,
    adminPermissionGroup: assertNonNil(findAdminPermissionGroup(groups)),
    patientFormsPermissionGroup: assertNonNil(findEFormsPermissionGroup(groups))
  };

  cachedDataUpdatedSignal.dispatch(_cachedData);
}


const queryOptions: ODataQueryParams = {
  expand: { "Users": {} }
}

function mapV3GroupToLegacyModel(group: IPermissionGroupV3): IPermissionGroup {
  // Use Users if it has data, otherwise map from UserPermissionGroupMemberships
  const users = (group.Users && group.Users.length > 0)
    ? group.Users
    : (group.UserPermissionGroupMemberships || [])
        .filter(m => !!m.UserId)
        .map(m => ({ RecordId: m.UserId } as IUser));

  return {
    // Legacy model fields expected by existing client logic.
    RecordId: String(group.PermissionGroupID ?? ""),
    RecordStatus: 0,
    Name: group.Name || "",
    Description: group.Description || "",
    UserRoleTypeCode: group.RoleTypeId,
    Users: users,
    // Unused by eForms access checks, but required by IPermissionGroup typing.
    PermissionSet1: 0,
    PermissionSet2: 0,
    PermissionSet3: 0,
    AdminPermissionSet: 0,
    AppointmentAdminPermissionSet: 0,
    ClinicalPermissionSet: 0,
    FinancialPermissionSet: 0,
    GeneralPermissionSet: 0,
    InsurancePermissionSet: 0,
    ReportsPermissionSet: 0,
    SpecialPermissionSet: 0,
  };
}


function isSchemaNotMigratedError(error: unknown): error is HttpError {
  if (!(error instanceof HttpError)) {
    return false;
  }

  return error.statusCode === 500 && /PermissionsV3\.PermissionGroups/i.test(error.message);
}


async function loadGroups() {
  try {
    const { apiUrl } = await loadConfig();
    const url = apiUrl.segment("PermissionsV3/GetPermissionGroups").toString();
    const { data } = await http.get<IPermissionGroupV3VM>(url);
    const v3Groups: IPermissionGroupV3[] = data.PermissionGroups || (data as any).permissionGroups || [];
    const groups: IPermissionGroup[] = v3Groups.map(mapV3GroupToLegacyModel);
    updateCachedValues(groups); // Side effect :(
    const entries = groups.map(g => [g.RecordId!, PermissionGroup_Factory.CreateIncoming(g)!] as [string,IPermissionGroup]);
    return new Map<string, IPermissionGroup>(entries);
  }
  catch (error) {
    if (isSchemaNotMigratedError(error)) {
      throw new SchemaNotMigratedError(error.message);
    }

    throw error;
  }
}


function getGroups() {
  return _cache.get("_");
}


export function refreshCachedPermissionData() {
  _cache.clear();
  return getGroups();
};


export async function getAllUserPermissionGroups() {
  if (!_cachedData) {
    await getGroups();
  }

  return _cachedData.userPermissionGroups;
}


export async function getUserPermissionGroups(userId: string) {
  console.assert(!!userId);
  const sets = await getAllUserPermissionGroups();
  return sets.get(userId) || [];
}



function cleanupModelForServerSubmission(permissionGroup: IPermissionGroup) {
  // Since `PermissionGroup.Users` collection contains a lot of unneeded data the server doesn't care about,
  // we'll clear out all fields other than the record ids.
  const prunedUsers = assertNonNil(permissionGroup.Users).map(user => ({ RecordId: user.RecordId! } as IUser));
  const model = { ...permissionGroup };
  model.Users = prunedUsers;
  return model;
}


export async function update(permissionGroup: IPermissionGroup) {
  const { oDataUrl } = await loadConfig();
  const api = new ODataApi(oDataUrl, "PermissionGroups");
  const model = cleanupModelForServerSubmission(permissionGroup);
  const groupId = permissionGroup.RecordId!;
  permissionGroup = await api.runEntityQuery<IPermissionGroup>(groupId, {method: "POST", data: model}, queryOptions);
  // Update cache
  //console.log("UPDATED: ", permissionGroup);
  // Groups should already have been loaded in the cache...
  const groups = await getGroups();
  groups.set(groupId, permissionGroup)
  updateCachedValues(Array.from(groups.values()));

  return permissionGroup;
}
